import React, { useContext, createContext, useState, useEffect, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { MyUser, FileUploadResult, UserAssetType, Role, ErrorCode } from '../types/types';
import API, { ApiError, isApiError } from '../api/api';
import { useAlert } from './AlertContext';

interface UserContextProps {
  children: any
}

// defining these separately to allow them to diverge
interface EntRoleData {
  profileIncomplete: boolean
}
export interface ConnectorRoleData {
  profileIncomplete: boolean
  fullName?: string
}

type RoleSpecificData = EntRoleData | ConnectorRoleData;

interface UserContextProviderProps {
  loading: boolean
  userInfo: MyUser | null
  userRole: Role | undefined
  roleSpecificData: RoleSpecificData | undefined
  createUserErrorCode: string | undefined
  setUserInfo: (p: any) => void
  refreshUserInfo: () => void
  getAccessToken: () => Promise<string>
  saveProfileImage: (image: FileUploadResult, investorProfileId?: number) => Promise<string | null>
  rootRedirect: string | null
  presentLogin: () => void
  userIsSelectingAvatar: boolean
  setUserIsSelectingAvatar: (isSelecting: boolean) => void
  updateRoleData: (roleData: RoleSpecificData) => void
}

export const UserContext = createContext<UserContextProviderProps>({
  userInfo: null,
  loading: false,
  userRole: undefined,
  roleSpecificData: undefined,
  createUserErrorCode: undefined,
  setUserInfo: () => {},
  refreshUserInfo: () => {},
  getAccessToken: () => Promise.resolve(''),
  saveProfileImage: async (image: FileUploadResult, investorProfileId?: number) => null,
  rootRedirect: null,
  presentLogin: () => {},
  userIsSelectingAvatar: false,
  setUserIsSelectingAvatar: (isSelecting: boolean) => {},
  updateRoleData: (roleData: RoleSpecificData) => {}
});

export const useUser = () => useContext(UserContext);

export function UserContextProvider(props: UserContextProps) {
  const [userInfo, setUserInfo] = useState<MyUser | null>(null);
  const { getAccessTokenSilently, isAuthenticated, logout } = useAuth0();
  const { user } = useAuth0();
  const [loading, setLoading] = useState(true);
  const [userIsSelectingAvatar, setUserIsSelectingAvatar] = useState(false);
  const { children } = props;
  const { postNewAlert } = useAlert();
  const [rootRedirect, setRootRedirect] = useState<string | null>(null);
  const [createUserErrorCode, setCreateUserErrorCode] = useState<string|undefined>();

  const getAccessToken = async (): Promise<string> => {
    try {
      return await getAccessTokenSilently();
    } catch (e: unknown) {
      postNewAlert('An unexpected error occurred. Please try again later.');
      console.log(e);
      throw e; // re-throw, it can't really be handled here (?)
    }
  };

  const userRole = useMemo(() => {
    if (userInfo) {
      return userInfo.role;
    }
    return undefined;
  }, [userInfo]);

  const [roleSpecificData, setRoleSpecificData] = useState<RoleSpecificData|undefined>(undefined);

  const updateRoleData = (roleData: RoleSpecificData) => {
    if (userInfo != null) {
      const { profileIncomplete } = roleData;

      if (userRole && userRole === Role.Connector) {
        const connectorData = roleData as ConnectorRoleData;
        const { fullName } = connectorData;
        setRoleSpecificData({ profileIncomplete, fullName });
      } else {
        setRoleSpecificData({ profileIncomplete });
      }
    }
  };

  const refreshUserInfo = async () => {
    let finishedLoading = true;
    try {
      setLoading(true);
      const userResponse = await API.getUser(await getAccessTokenSilently());
      setUserInfo(userResponse);
    } catch (error: unknown) {
      console.error(error);
      if (isApiError(error)) {
        const apiError = error as ApiError;
        console.log('API error', JSON.stringify(error));
        if (apiError.code === ErrorCode.UserNotFound) {
          finishedLoading = false;
          await handleCreateUser();
        } else {
          throw error; // re-throw anything except the specific errors we can handle above.
        }
      } else {
        throw error; // re-throw anything except the specific errors we can handle above.
      }
    } finally {
      if (finishedLoading) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const getAccessTokenAndUser = async () => {
      try {
        await refreshUserInfo();
      } catch (e: unknown) {
        console.error('An error occurred refreshing user data: ', e);
      }
    };
    if (isAuthenticated) {
      getAccessTokenAndUser();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAccessTokenSilently, isAuthenticated]);

  const handleCreateUser = async () => {
    try {
      const token = await getAccessToken();
      await API.createEntUser(token, {
        firstName: user.given_name || '',
        lastName: user.family_name || '',
        role: Role.Ent,
        email: user.email!
      });
      refreshUserInfo();
    } catch (e: unknown) {
      console.log('Error in handleCreateUser', e, isApiError(e));
      if (isApiError(e) && (e as ApiError).code === ErrorCode.UserAlreadyExistsEmail) {
        console.log('User already exists error');
        setCreateUserErrorCode(ErrorCode.UserAlreadyExistsEmail);
      }
      postNewAlert('An unexpected error occurred during user creation.');
    } finally {
      setLoading(false);
    }
  };

  const saveProfileImage = async (image: FileUploadResult, investorProfileId?: number):Promise<string | null> => {
    try {
      const input = investorProfileId ? {
        image: image.data,
        mime: image.mime,
        investorProfileId,
        type: UserAssetType.Avatar
      } : {
        image: image.data,
        mime: image.mime,
        type: UserAssetType.Avatar
      };

      const { location } = await API.uploadImageAsset(await getAccessToken(), input);
      if (location) {
        return location;
      }
    } catch (error: unknown) {
      console.log('error updating profile picture: ', error);
      postNewAlert('An error occurred while updating the profile picture.  Please try again later.');
    }
    return null;
  };

  const presentLogin = () => {
    if (!isAuthenticated) {
      setRootRedirect('/');
    }
  };

  return (
    <UserContext.Provider value={{
      userInfo,
      loading,
      userRole,
      roleSpecificData,
      createUserErrorCode,
      setUserInfo: (p: any) => setUserInfo(p),
      refreshUserInfo: () => refreshUserInfo(),
      getAccessToken: () => getAccessToken(),
      saveProfileImage,
      rootRedirect,
      presentLogin,
      userIsSelectingAvatar,
      setUserIsSelectingAvatar,
      updateRoleData
    }}
    >
      {children}
    </UserContext.Provider>
  );
}
